<template>
  <CustomModal :is-open="isOpen" @close="$emit('close')">
    <div class="w-full max-w-md">
      <div class="flex flex-col items-center justify-center gap-y-1">
        <img :src="adBetaIcon" alt="Boost Icon" class="h-10 w-10 mb-2" />
        <p
          class="text-xs text-primary rounded-full bg-red-100 dark:bg-red-950 px-2 py-1"
        >
          Beta
        </p>
        <div class="flex items-center justify-center">
          <p class="text-lg text-black dark:text-white">
            Boost {{ showSuccessMessage ? "request sent" : "Your Post" }}
          </p>
        </div>
      </div>

      <p
        v-if="showSuccessMessage"
        class="text-xs md:text-sm text-center mb-2 text-black dark:text-white"
      >
        Your request has been submitted. We'll notify you once it's reviewed.
      </p>
      <p
        v-else
        class="text-xs md:text-sm text-center mb-2 text-black dark:text-white"
      >
        More reach. More engagement. Just $29/day or $24/day if you pay with
        OPT!
      </p>
      <button
        v-if="showSuccessMessage"
        @click="handleClose"
        class="text-md md:text-lg h-8 w-full rounded-full bg-primary text-white"
      >
        Explore Parler
      </button>

      <form
        v-if="!showSuccessMessage"
        @submit.prevent="submitBoost"
        class="gap-y-2 pb-4"
      >
        <div class="space-y-2">
          <ion-label
            class="font-semibold text-sm md:text-md text-black dark:text-white"
            >1. Enter your email address</ion-label
          >
          <ion-input
            v-model="boostEmail"
            :disabled="
              Boolean(authStore.currentUser.email) && !isAutoGeneratedEmail
            "
            type="email"
            placeholder="Email address"
            class="w-full rounded-lg border dark:border-gray-700 text-sm md:text-md text-black dark:text-white"
            :class="{
              'border-red-500 dark:border-red-500':
                boostEmail.value && !isValidEmail,
            }"
            required
          ></ion-input>
          <p
            v-if="boostEmail.value && !isValidEmail"
            class="text-xs text-red-500 mt-1"
          >
            Please enter a valid email address
          </p>
        </div>

        <div class="space-y-2">
          <ion-label
            class="font-semibold text-sm md:text-md text-black dark:text-white"
            >2. Choose start & end date:</ion-label
          >
          <div class="flex flex-col gap-y-3">
            <div class="flex gap-x-2 items-center justify-between">
              <ion-label class="text-xs md:text-sm text-black dark:text-white">
                Start:
              </ion-label>
              <div
                class="text-xs md:text-sm text-center cursor-pointer text-black dark:text-white border border-gray-300 dark:border-gray-700 rounded-lg px-2 py-1"
                @click="showStartDatePicker = true"
              >
                {{ formatDateTime(boostStartDate) }}
              </div>
            </div>
            <div class="flex gap-x-2 items-center justify-between">
              <ion-label class="text-xs md:text-sm text-black dark:text-white">
                End:
              </ion-label>
              <div
                class="text-xs md:text-sm text-center cursor-pointer text-black dark:text-white border border-gray-300 dark:border-gray-700 rounded-lg px-2 py-1"
                @click="showEndDatePicker = true"
              >
                {{ formatDateTime(boostEndDate) }}
              </div>
            </div>
          </div>
        </div>

        <div class="space-y-2">
          <ion-label
            class="font-semibold text-sm md:text-md text-black dark:text-white"
          >
            3. Choose your goal:
          </ion-label>
          <ion-list class="space-y-1 w-full rounded-2xl">
            <ion-item
              lines="none"
              :class="boostGoals.moreViews ? 'border border-primary' : 'p-px'"
            >
              <div class="h-full">
                <ion-checkbox
                  v-model="boostGoals.moreViews"
                  label-placement="end"
                  @ionChange="handleGoalChange('moreViews', $event)"
                >
                  <p class="text-xs md:text-sm text-black dark:text-white">
                    More views
                  </p>
                </ion-checkbox>
              </div>
            </ion-item>
            <ion-item
              lines="none"
              :class="
                boostGoals.moreFollowers ? 'border border-primary' : 'p-px'
              "
            >
              <div class="h-full">
                <ion-checkbox
                  v-model="boostGoals.moreFollowers"
                  label-placement="end"
                  @ionChange="handleGoalChange('moreFollowers', $event)"
                >
                  <p class="text-xs md:text-sm text-black dark:text-white">
                    More followers
                  </p>
                </ion-checkbox>
              </div>
            </ion-item>
            <ion-item
              lines="none"
              :class="boostGoals.moreClicks ? 'border border-primary' : 'p-px'"
            >
              <div class="h-full">
                <ion-checkbox
                  v-model="boostGoals.moreClicks"
                  label-placement="end"
                  @ionChange="handleGoalChange('moreClicks', $event)"
                >
                  <p class="text-xs md:text-sm text-black dark:text-white">
                    More clicks
                  </p>
                </ion-checkbox>
              </div>
            </ion-item>
          </ion-list>
        </div>
        <p
          class="text-[0.6rem] md:text-sm text-center text-black dark:text-white mt-1"
        >
          <span class="font-bold">Estimated charge:</span>
          {{ calculatePrice() }}
        </p>

        <div class="text-center my-1">
          <button
            type="submit"
            class="text-md md:text-lg h-8 w-full rounded-full bg-primary text-white"
            :disabled="isSubmitDisabled"
            :class="{ 'opacity-50 cursor-not-allowed': isSubmitDisabled }"
          >
            Submit Request
          </button>
        </div>
      </form>
    </div>
  </CustomModal>

  <!-- Start Date Picker Modal -->
  <ion-modal
    :is-open="showStartDatePicker"
    @didDismiss="showStartDatePicker = false"
    :initialBreakpoint="isMobile ? 0.25 : 0.5"
    :breakpoints="[0, isMobile ? 0.25 : 0.5]"
  >
    <ion-content
      scroll-behavior="contain"
      class="ion-padding flex justify-center items-center w-full"
    >
      <ion-datetime
        v-model="boostStartDate"
        presentation="date-time"
        :prefer-wheel="isMobile"
        @ionChange="onStartDateChange"
        class="mx-auto"
      ></ion-datetime>
    </ion-content>
  </ion-modal>

  <!-- End Date Picker Modal -->
  <ion-modal
    :is-open="showEndDatePicker"
    @didDismiss="showEndDatePicker = false"
    :initialBreakpoint="isMobile ? 0.25 : 0.5"
    :breakpoints="[0, isMobile ? 0.25 : 0.5]"
  >
    <ion-content class="ion-padding flex justify-center items-center w-full">
      <ion-datetime
        v-model="boostEndDate"
        presentation="date-time"
        :prefer-wheel="isMobile"
        @ionChange="onEndDateChange"
        class="mx-auto"
      ></ion-datetime>
    </ion-content>
  </ion-modal>

  <ion-toast
    :is-open="toastOpen"
    :message="toastMessage"
    :duration="3000"
    :color="toastColor"
    @didDismiss="toastOpen = false"
  ></ion-toast>
</template>

<script setup>
import {
  IonInput,
  IonLabel,
  IonCheckbox,
  IonDatetime,
  IonModal,
  IonItem,
  IonList,
  IonContent,
  IonToast,
} from "@ionic/vue";
import { ref, defineEmits, defineProps, computed } from "vue";
import CustomModal from "@/components/CustomModal.vue";
import adBetaIcon from "@/assets/ad_beta.svg";
import { useAuthStore } from "@/stores/auth";
import { usePostStore } from "@/stores/post";
import { apiBackendAuthAxios } from "@/axiosAuth";

const props = defineProps({
  isOpen: {
    type: Boolean,
    required: true,
  },
  postId: {
    type: String,
    required: true,
  },
});

const emit = defineEmits(["close", "submitted"]);

// Boost modal states
const authStore = useAuthStore();
const postStore = usePostStore();

// Get domain for post URL
const apiHost = import.meta.env.VITE_API_HOST;
const domain = apiHost.substring(apiHost.indexOf(".") + 1);

// Check if the email is auto-generated
const isAutoGeneratedEmail = computed(() => {
  if (!authStore.currentUser) return false;
  const email = authStore.currentUser.email || "";
  return email.match(/^user-\+[0-9]+@example\.com$/i) !== null;
});

// Calculate initial minimum start date
const getMinValidStartDate = () => {
  const now = new Date();
  now.setHours(now.getHours() + 24);
  return now.toISOString();
};

// Initialize email field - make it blank if auto-generated
const boostEmail = ref(
  !authStore.currentUser
    ? ""
    : isAutoGeneratedEmail.value
    ? ""
    : authStore.currentUser.email || ""
);
const boostStartDate = ref(getMinValidStartDate());
const boostEndDate = ref(
  (() => {
    const initialStart = new Date(getMinValidStartDate());
    initialStart.setHours(initialStart.getHours() + 24); // Add exactly 24 hours
    return initialStart.toISOString();
  })()
);
const boostGoals = ref({
  moreViews: true,
  moreFollowers: false,
  moreClicks: false,
});

// Date picker visibility states
const showStartDatePicker = ref(false);
const showEndDatePicker = ref(false);

// Success message state
const showSuccessMessage = ref(false);

// Toast state
const toastOpen = ref(false);
const toastMessage = ref("");
const toastColor = ref("danger");
const isMobile = computed(() => {
  return window.innerWidth < 768;
});

// Email validation computed property
const isValidEmail = computed(() => {
  if (!boostEmail.value) return false;

  // Basic email regex validation
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return emailRegex.test(boostEmail.value);
});

// Computed property to determine if submit button should be disabled
const isSubmitDisabled = computed(() => {
  return !isValidEmail.value;
});

const showToast = (message, color = "danger") => {
  toastMessage.value = message;
  toastColor.value = color;
  toastOpen.value = true;
};

// Date handling functions
const formatDateTime = (isoDate) => {
  if (!isoDate) return "";
  const date = new Date(isoDate);
  return (
    date.toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
    }) +
    " " +
    date.toLocaleTimeString("en-US", {
      hour: "numeric",
      minute: "numeric",
      hour12: true,
    })
  );
};

// Format date to CST for email report
const formatDateTimeCST = (isoDate) => {
  if (!isoDate) return "";

  // Create a date object from the ISO string
  const date = new Date(isoDate);

  // Format the date to CST timezone
  return new Intl.DateTimeFormat("en-US", {
    month: "short",
    day: "numeric",
    hour: "numeric",
    minute: "numeric",
    hour12: true,
    timeZone: "America/Chicago",
  }).format(date);
};

const calculateDays = () => {
  const start = new Date(boostStartDate.value);
  const end = new Date(boostEndDate.value);
  // Calculate difference in days, rounding up to include partial days
  return Math.ceil((end - start) / (1000 * 60 * 60 * 24));
};

const onStartDateChange = (event) => {
  const selectedISO = event.detail.value;
  const selectedDate = new Date(selectedISO);

  // Calculate minimum allowed start date (now + 24 hours)
  const minStartDate = new Date();
  minStartDate.setHours(minStartDate.getHours() + 24);

  if (selectedDate < minStartDate) {
    // If invalid, show toast and reset
    showToast("Start date must be at least 24 hours from now.");
    boostStartDate.value = minStartDate.toISOString(); // Reset to minimum valid
    // Adjust end date based on the reset start date
    const minEndDate = new Date(boostStartDate.value);
    minEndDate.setHours(minEndDate.getHours() + 24);
    if (new Date(boostEndDate.value) < minEndDate) {
      boostEndDate.value = minEndDate.toISOString();
    }
    return; // Exit early
  }

  // If valid, update the start date
  boostStartDate.value = selectedISO;

  // Update end date if it's before the new start date + 24h
  const minEndDate = new Date(boostStartDate.value); // Recalculate min end date
  minEndDate.setHours(minEndDate.getHours() + 24);
  const endDate = new Date(boostEndDate.value);

  if (endDate < minEndDate) {
    boostEndDate.value = minEndDate.toISOString();
  }
};

const onEndDateChange = (event) => {
  const selectedISO = event.detail.value;
  const selectedDate = new Date(selectedISO);

  // Calculate minimum allowed end date (start date + 24 hours)
  const minEndDate = new Date(boostStartDate.value);
  minEndDate.setHours(minEndDate.getHours() + 24);

  if (selectedDate < minEndDate) {
    // If invalid, show toast and reset
    showToast("End date must be at least 24 hours after the start date.");
    boostEndDate.value = minEndDate.toISOString(); // Reset to minimum valid
    return; // Exit early
  }

  // If valid, update the end date
  boostEndDate.value = selectedISO;
};

// Function to calculate the price based on date range
const calculatePrice = () => {
  return (
    "$" +
    (calculateDays() * 29).toFixed(2) +
    " or $" +
    (calculateDays() * 24).toFixed(2) +
    " if you pay with OPT!"
  );
};

// Function to submit the boost request
const submitBoost = async () => {
  // Keep the full boostData for potential event emission or other uses
  const boostData = {
    postId: props.postId,
    email: boostEmail.value,
    startDate: boostStartDate.value,
    endDate: boostEndDate.value,
    days: calculateDays(),
    goal: Object.keys(boostGoals.value).find(
      (key) => boostGoals.value[key] === true
    ),
    price: calculatePrice(),
  };

  // Helper function to extract and display error messages
  const handleError = (error) => {
    console.error("Boost error:", error);

    // Extract the actual error message from different possible error formats
    let errorMessage =
      "Your submission could not be sent, please contact support.";

    // Case 1: Error from boostResult
    if (error.error) {
      errorMessage = error.error;
    }
    // Case 2: Error is an Axios error with response data
    else if (error.response?.data) {
      if (error.response.data.error) {
        errorMessage = error.response.data.error;
      } else if (error.response.data.message) {
        errorMessage = error.response.data.message;
      } else if (typeof error.response.data === "string") {
        errorMessage = error.response.data;
      }
    }
    // Case 3: Regular Error object with message property
    else if (error.message) {
      errorMessage = error.message;
    }

    // Check if the error message indicates post is already boosted
    if (
      errorMessage.toLowerCase().includes("already boosted") ||
      errorMessage.toLowerCase().includes("selected period")
    ) {
      showToast(
        "This post is already boosted during the selected time period. Please choose different dates.",
        "danger"
      );
    } else {
      showToast(errorMessage, "danger");
    }
  };

  try {
    // 1. Call the boost action from the post store
    const boostResult = await postStore.boostPostAction(
      props.postId,
      boostStartDate.value,
      boostEndDate.value
      // adType defaults to 'standard' in the store action
    );

    if (boostResult.success) {
      showToast("Boost request sent!", "success");

      // Set success message to true immediately after the boost is successful
      showSuccessMessage.value = true;

      try {
        // Send email to support using the feedback route
        const postLink = `https://app.${domain}/post/${props.postId}?ref=${authStore.currentUser.referral_code}`;
        const isInfluencer =
          authStore.currentUser.badges?.includes("gold_verified");

        // Calculate the cost
        const rawCost = (calculateDays() * 29).toFixed(2);

        // Format the start and end dates for display in CST
        const startDateCST = formatDateTimeCST(boostStartDate.value);
        const endDateCST = formatDateTimeCST(boostEndDate.value);

        // Create the goal name with capitalized first letter
        const goalName = Object.keys(boostGoals.value).find(
          (key) => boostGoals.value[key] === true
        );

        // Format the goal to match the screenshot (e.g., "moreViews" -> "More views")
        const formattedGoal =
          goalName === "moreViews"
            ? "More views"
            : goalName === "moreFollowers"
            ? "More followers"
            : "More clicks";

        // Get current time in CST for submission timestamp
        const submissionTimeCST = new Intl.DateTimeFormat("en-US", {
          month: "short",
          day: "numeric",
          hour: "numeric",
          minute: "numeric",
          hour12: true,
          timeZone: "America/Chicago",
        }).format(new Date());

        // Calculate duration in hours for shorter boosts
        const durationHours = calculateDays() * 24;
        const durationText =
          durationHours <= 72
            ? `${durationHours} hours`
            : `${calculateDays()}d`;

        // Add a clear subject line in the message itself
        const emailSubject = `New Boost Request: ${authStore.currentUser.username}`;

        console.log(authStore.currentUser);

        // Create report message with enhanced format
        const report = {
          message: `${emailSubject}

Boost request - ID: ${props.postId}
User: ${authStore.currentUser.username}${isInfluencer ? " (Gold Verified)" : ""}
Email: ${boostEmail.value}
Submitted: ${submissionTimeCST} CST
Duration: ${startDateCST}-${endDateCST} (${durationText})
Goal: ${formattedGoal}
Cost: $${rawCost}

${postLink}

--------------------
USER INFO:
Username: ${authStore.currentUser.username}
Display Name: ${authStore.currentUser.name || authStore.currentUser.username}
Email: ${boostEmail.value}
Phone: ${authStore.currentUser.phone_number || ""}
`,
        };
        await apiBackendAuthAxios.post("/feedback", report);
      } catch (error) {
        console.error("Error contacting support:", error);
        showToast(
          "Failed to notify support team, but your boost request was submitted.",
          "warning"
        );
      }

      // Emit the submitted event with the boostData
      emit("submitted", boostData);

      // Reset form fields
      resetFormFields();
    } else {
      // Handle unsuccessful boost result
      console.log("Boost result:", boostResult);
      handleError(boostResult.originalError || boostResult);
    }
  } catch (error) {
    // Handle unexpected errors during the process
    handleError(error);
  }
};

// Function to handle closing the modal
const handleClose = () => {
  // Reset the form and close the modal
  showSuccessMessage.value = false;
  resetFormFields();
  emit("close");
};

// Helper function to reset form fields
const resetFormFields = () => {
  if (!authStore.currentUser.email || isAutoGeneratedEmail.value) {
    boostEmail.value = "";
  }
  boostStartDate.value = getMinValidStartDate();
  const minEnd = new Date(boostStartDate.value);
  minEnd.setHours(minEnd.getHours() + 24);
  boostEndDate.value = minEnd.toISOString();
  boostGoals.value = {
    moreViews: true,
    moreFollowers: false,
    moreClicks: false,
  };
};

// Add handler function for goal checkboxes
const handleGoalChange = (goalKey, event) => {
  const isChecked = event.detail.checked;

  if (isChecked) {
    // Uncheck all other goals
    for (const key in boostGoals.value) {
      if (key !== goalKey) {
        boostGoals.value[key] = false;
      }
    }
  } else {
    // Prevent unchecking the last selected goal
    // Check if this was the only checked item
    let anyOtherChecked = false;
    for (const key in boostGoals.value) {
      if (key !== goalKey && boostGoals.value[key]) {
        anyOtherChecked = true;
        break;
      }
    }
    // If no other item is checked, force this one back to true
    if (!anyOtherChecked) {
      boostGoals.value[goalKey] = true;
    }
  }
};
</script>

<style scoped>
.text-primary {
  color: var(--primary-color);
}

.bg-primary {
  background-color: var(--primary-color);
}

.border-primary {
  border: 1px solid var(--primary-color);
  border-radius: 16px;
}

ion-checkbox {
  --size: 24px;
  --checkbox-background-checked: var(--primary-color);
  --border-color: var(--ion-color-medium-shade, #808289);
  --border-radius: 4px;
  --border-width: 2px;
  --background: white;
  --background-checked: var(--primary-color);
  margin-right: 8px;
}

ion-checkbox::part(container) {
  border-radius: 6px;
  border: 2px solid var(--ion-color-medium-shade, #808289);
}

.no-overscroll {
  overscroll-behavior: contain;
}

.email-input {
  --padding-start: 12px;
  --padding-end: 12px;
}
</style>
